import React, { useState, useRef } from "react";
import { httpClient, authFetch } from '../../../../HttpClient';
import { uploadFileToBlob } from '../../../../utils/uploadFileToBlob';
import { dialog } from '../../../../components/ui/Dialog';
import {
  ChevronRight,
  Check,
  User,
  FileText,
  Bell,
  CreditCard,
  Shield,
  ArrowDownToLine,
  Plus,
  X,
  KeyRound,
  Eye,
  EyeOff,
  Trash2,
  Pencil,
  Globe,
  Info,
} from "lucide-react";
import { showStatusToast } from "../../../../components/ui/Toast";

const TNS_PERIODICITES = ["Mensuelle", "Trimestrielle", "Semestrielle", "Annuelle"];
// Caisse de COTISATION — distincte de la caisse de retraite ci-dessous.
const TNS_ORGANISMES = ["URSSAF", "CGSS Réunion"];
const TNS_CAISSES_RETRAITE = ["CARPIMKO", "RCI", "CIPAV", "CARMF", "CARCDSF", "IRCEC"];
const TNS_PAIEMENTS = ["Prélèvement", "Virement", "Télépaiement"];

// Liste déroulante dont la valeur courante reste affichée même si elle ne
// fait pas (ou plus) partie des choix proposés.
function withCurrent(options, current) {
  return current && !options.includes(current) ? [...options, current] : options;
}

// Interrupteur on/off — mêmes services (TNS/IR) que l'overlay de détails
// Annuaire.js, ici intégré au flux de formulaire (sauvegardé avec le reste).
function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0 ${
        checked ? "bg-[#7A1533]" : "bg-slate-200"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[18px]" : "translate-x-[2px]"
        }`}
      />
    </button>
  );
}

const COMPTE_TYPE_LABELS = {
  IMPOT_GOUV_PARTICULIER: "Impôt.gouv Particulier",
  FEDER: "FEDER",
  BPI: "BPI",
  CESU: "CESU",
  SSI_EN_LIGNE: "SSI en ligne",
  IMPOT_GOUV_PRO_NON_RATTACHE: "Impôt.gouv Pro non rattaché",
  TESE: "TESE",
  ASP_SYLAE: "ASP SYLAE",
  OPCO: "OPCO",
  CHORUS_PRO: "CHORUS PRO",
  CHOMAGE_PARTIEL: "Chômage Partiel",
  NET_ENTREPRISE: "Net Entreprise",
  AUTOENTREPRENEUR: "Auto-entrepreneur",
  PROBTP: "PROBTP",
  CIPAV: "CIPAV",
  CARDSF: "CARDSF",
  CARMF: "CARMF",
  CARPIMKO: "CARPIMKO",
  AMELI_PRO: "Ameli Pro",
  DEFIMODE: "DEFIMODE",
  MACSF: "MACSF",
  RAAP_IRCEC: "RAAP IRCEC",
  INSEE: "INSEE",
  DIVERS: "Divers",
};

// Idem FicheClientOverlay.js/Annuaire.js : le badge "Divers" montre le nom
// personnalisé donné à la création (Compte.diversLabel).
function getCompteTypeLabel(compte) {
  if (compte.type === "DIVERS") {
    return compte.diversLabel
      ? `${COMPTE_TYPE_LABELS.DIVERS} : ${compte.diversLabel}`
      : COMPTE_TYPE_LABELS.DIVERS;
  }
  return COMPTE_TYPE_LABELS[compte.type] || compte.type;
}

const COMPTES_API = "https://backend-myalfa.vercel.app/api/comptes";

const QUESTIONS = [
  "J'ai rencontré la personne et j'atteste de la véracité de son identité",
  "S'agit-il d'une personne politiquement exposée (PPE) ?",
  "Existe-t-il des doutes sur l'intégrité et la réputation de cette personne ?",
  "Cette personne figure-t-elle sur l'une des listes concernant le gel des avoirs ?",
];

/* ── Atoms ── */
function SectionCard({ icon: Icon, title, suffix, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
          {title}
        </span>
        {suffix && <span className="text-[10px] text-slate-400">{suffix}</span>}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  placeholder,
  value,
  onChange,
  required,
  type = "text",
  half,
  hasError,
  note,
}) {
  return (
    <div className={half ? "" : "col-span-2"}>
      <label
        className={`block text-[11px] mb-1 ${
          hasError ? "text-rose-500 font-medium" : "text-slate-500"
        }`}
      >
        {label}
        {required && " *"}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full border rounded-lg px-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-1 bg-white transition-colors ${
          hasError
            ? "border-rose-300 focus:ring-rose-300/40 bg-rose-50/30"
            : "border-slate-200 focus:ring-[#7A1533]/20"
        }`}
      />
      {hasError && (
        <p className="text-[10px] text-rose-500 mt-0.5">Ce champ est requis</p>
      )}
      {!hasError && note && (
        <p className="text-[10px] text-slate-400 mt-0.5">{note}</p>
      )}
    </div>
  );
}

function UploadZone({
  label,
  subtitle,
  required,
  file,
  onChange,
  existingUrl,
}) {
  const inputRef = useRef();
  const hasExisting = existingUrl && !file;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed cursor-pointer hover:bg-slate-100/40 transition-colors ${
        hasExisting
          ? "border-emerald-300 bg-emerald-50/30"
          : "border-slate-300 bg-slate-50/50"
      }`}
      onClick={() => inputRef.current.click()}
    >
      <ArrowDownToLine
        className={`w-4 h-4 shrink-0 ${
          hasExisting ? "text-emerald-600" : "text-[#7A1533]"
        }`}
      />
      <div className="flex-1 min-w-0">
        <p
          className={`text-xs font-semibold leading-tight truncate ${
            hasExisting ? "text-emerald-700" : "text-slate-700"
          }`}
        >
          {file
            ? file.name
            : hasExisting
            ? "Fichier existant — cliquer pour remplacer"
            : label}
          {required && !file && !existingUrl && " *"}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => onChange(e.target.files[0] || null)}
      />
    </div>
  );
}

function QuestionRow({
  num,
  question,
  answer,
  onAnswerChange,
  comment,
  onCommentChange,
}) {
  const [showComment, setShowComment] = useState(false);
  return (
    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="text-xs font-bold text-[#7A1533] w-4 shrink-0">
          {num}
        </span>
        <p className="flex-1 text-xs text-slate-700 leading-snug">{question}</p>
        <div className="flex items-center gap-1 shrink-0">
          {["Oui", "Non", "N/A"].map((opt) => {
            const activeStyle =
              opt === "Oui"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 font-medium"
                : opt === "Non"
                ? "border-rose-200 bg-rose-50 text-rose-600 font-medium"
                : "border-slate-300 bg-slate-100 text-slate-700 font-bold";
            return (
              <button
                key={opt}
                onClick={() => onAnswerChange(opt)}
                className={`text-[11px] px-2.5 py-1 rounded-lg border transition-colors ${
                  answer === opt
                    ? activeStyle
                    : "border-slate-200 text-slate-400 hover:border-slate-300"
                }`}
              >
                {opt}
              </button>
            );
          })}
          <button
            onClick={() => setShowComment((v) => !v)}
            className={`ml-1 transition-colors ${
              showComment
                ? "text-[#7A1533]"
                : "text-slate-300 hover:text-slate-500"
            }`}
          >
            <Plus
              className={`w-3.5 h-3.5 transition-transform ${
                showComment ? "rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {showComment && (
        <div className="px-4 pb-3 pt-1" style={{ background: "#faf9f7" }}>
          <textarea
            rows={2}
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder="Commentaire (facultatif)..."
            className="w-full text-[11px] rounded-lg px-2.5 py-1.5 text-slate-600 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 border-0"
            style={{ background: "#faf9f7" }}
          />
        </div>
      )}
    </div>
  );
}

const ANNUAIRE_API = "https://backend-myalfa.vercel.app/api/gi/annuaire";

/* ── Main form ── */
export default function NouveauContactForm({
  clientNom = "MINA PIZZERIA",
  clientId,
  contact,
  contactId,
  onContactCreated,
  onBack,
  onCancel,
}) {
  const isEditMode = !!contactId;

  const [form, setForm] = useState({
    nom: contact?.nom || "",
    prenoms: contact?.prenoms || "",
    dateNaissance: contact?.dateNaissance || "",
    villeNaissance: contact?.villeNaissance || "",
    paysNaissance: contact?.paysNaissance || "",
    rue: contact?.rue || "",
    codePostal: contact?.codePostal || "",
    villeAdresse: contact?.villeAdresse || "",
    paysAdresse: contact?.paysAdresse || "France",
    email: contact?.email || "",
    telephone: contact?.telephone || "",
    dateEmission: contact?.dateEmission || "",
    dateExpiration: contact?.dateExpiration || "",
    autresMissions: contact?.autresMissions || false,
    questionnaireAnswers: [
      contact?.question1Reponse || "",
      contact?.question2Reponse || "",
      contact?.question3Reponse || "",
      contact?.question4Reponse || "",
    ],
    questionnaireComments: [
      contact?.question1Commentaire || "",
      contact?.question2Commentaire || "",
      contact?.question3Commentaire || "",
      contact?.question4Commentaire || "",
    ],
    commentaireGlobal: contact?.commentaireGlobal || "",
    nationalite: contact?.nationalite || "",
    autresAdresses: contact?.autresAdresses || "",
    identiteVerifiee: contact?.identiteVerifiee || false,
    autreActivite: contact?.autreActivite || "",
    provenance: contact?.provenance || "Autre",
    estTNS: contact?.estTNS || false,
    tnsPeriodicite: contact?.tnsPeriodicite || TNS_PERIODICITES[1],
    tnsRegime: contact?.tnsRegime || "",
    // Une fiche déjà suivie en TNS garde ses champs vides tels quels (pas de
    // valeur imposée à l'enregistrement) ; sinon valeurs par défaut.
    tnsOrganisme: contact?.estTNS ? contact.tnsOrganisme || "" : contact?.tnsOrganisme || TNS_ORGANISMES[0],
    tnsPaiement: contact?.estTNS ? contact.tnsPaiement || "" : contact?.tnsPaiement || TNS_PAIEMENTS[0],
    tnsCaisseRetraite: contact?.tnsCaisseRetraite || "",
    tnsCommentaire: contact?.tnsCommentaire || "",
  });

  // Accès MyALFA Link : l'identifiant est l'email du contact (même login et
  // même mot de passe que l'onglet MyALFA Link de la fiche client).
  const accesInitial = contact?.accesLink ?? (!!contact?.mdp && !contact?.accesLinkDesactive);
  const [accesLink, setAccesLink] = useState(!!accesInitial);
  const [linkMdp, setLinkMdp] = useState(contact?.mdp || "");
  const [voirLinkMdp, setVoirLinkMdp] = useState(false);
  const [linkErr, setLinkErr] = useState(false);
  // Contact qui a choisi son mot de passe lui-même sur le portail : il peut
  // rester vide (le mot de passe du contact est conservé).
  const mdpPortailConserve = !!contact?.comptePortail && !contact?.mdp;

  const [pieceIdentiteFile, setPieceIdentiteFile] = useState(null);
  const [extraitRBEFile, setExtraitRBEFile] = useState(null);
  const [autresJustificatifs, setAutresJustificatifs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Comptes ──────────────────────────────────────────────────────────────────
  const [localComptes, setLocalComptes] = useState(contact?.comptes || []);
  const [newCompteInForm, setNewCompteInForm] = useState({ type: "", identifiant: "", motDePasse: "", diversLabel: "" });
  const [showComptePassInForm, setShowComptePassInForm] = useState({});
  const [compteFormErr, setCompteFormErr] = useState("");
  const [editingCompteKeyInForm, setEditingCompteKeyInForm] = useState(null);
  const [editingCompteDataInForm, setEditingCompteDataInForm] = useState({ identifiant: "", motDePasse: "", diversLabel: "" });
  const [selectedClients, setSelectedClients] = useState(
    contact?.clients?.map((c) => ({
      id: c.id,
      nomClient: c.nom,
    })) || (clientId ? [{ id: clientId, nomClient: clientNom }] : [])
  );
  const [clientSearch, setClientSearch] = useState("");
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [clientError, setClientError] = useState(false);
  const justificatifInputRef = useRef();

  const handleClientSearch = async (e) => {
    const query = e.target.value;
    setClientSearch(query);
    if (query.length < 2) {
      setClientSuggestions([]);
      return;
    }
    try {
      const res = await authFetch(`https://backend-myalfa.vercel.app/api/clients/search?search=${encodeURIComponent(
          query
        )}`
      );
      const data = await res.json();
      setClientSuggestions(Array.isArray(data) ? data : []);
    } catch {
      setClientSuggestions([]);
    }
  };

  const selectClient = (client) => {
    if (!selectedClients.find((c) => c.id === client.id)) {
      setSelectedClients((prev) => [
        ...prev,
        { id: client.id, nomClient: client.nom },
      ]);
      setClientError(false);
    }
    setClientSearch("");
    setClientSuggestions([]);
  };

  const removeClient = (id) =>
    setSelectedClients((prev) => prev.filter((c) => c.id !== id));

  const setField = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (fieldErrors[key])
      setFieldErrors((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
  };

  const setAnswer = (i, val) =>
    setForm((f) => {
      const arr = [...f.questionnaireAnswers];
      arr[i] = val;
      return { ...f, questionnaireAnswers: arr };
    });

  const setComment = (i, val) =>
    setForm((f) => {
      const arr = [...f.questionnaireComments];
      arr[i] = val;
      return { ...f, questionnaireComments: arr };
    });

  const handleAddJustificatif = (e) => {
    const file = e.target.files[0];
    if (file) setAutresJustificatifs((prev) => [...prev, file]);
    e.target.value = "";
  };

  const REQUIRED_IDENTITY = ["nom", "prenoms", "dateNaissance"];

  const handleSave = async () => {
    setError(null);

    const errors = {};
    REQUIRED_IDENTITY.forEach((key) => {
      if (!form[key].trim()) errors[key] = true;
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(
        "Veuillez renseigner tous les champs obligatoires de la section Identité."
      );
      return;
    }
    setFieldErrors({});
    if (accesLink && (!form.email.trim() || (!linkMdp.trim() && !mdpPortailConserve))) {
      setLinkErr(true);
      setError("Accès à MyALFA Link : renseignez l'email et le mot de passe, ou décochez l'accès.");
      return;
    }
    setLinkErr(false);
    setClientError(false);
    setSaving(true);

    const fd = new FormData();

    fd.append("nom", form.nom);
    fd.append("prenoms", form.prenoms);
    fd.append("dateNaissance", form.dateNaissance);
    fd.append("villeNaissance", form.villeNaissance);
    fd.append("paysNaissance", form.paysNaissance);
    fd.append("rue", form.rue);
    fd.append("codePostal", form.codePostal);
    fd.append("villeAdresse", form.villeAdresse);
    fd.append("paysAdresse", form.paysAdresse);
    fd.append("email", form.email);
    fd.append("telephone", form.telephone);
    fd.append("dateEmission", form.dateEmission);
    fd.append("dateExpiration", form.dateExpiration);
    fd.append("autresMissions", form.autresMissions);
    fd.append("nationalite", form.nationalite);
    fd.append("autresAdresses", form.autresAdresses);
    fd.append("identiteVerifiee", form.identiteVerifiee);
    fd.append("autreActivite", form.autreActivite);
    fd.append("provenance", form.provenance || "Autre");
    fd.append("estTNS", form.estTNS);
    fd.append("tnsPeriodicite", form.tnsPeriodicite);
    fd.append("tnsRegime", form.tnsRegime);
    fd.append("tnsOrganisme", form.tnsOrganisme);
    fd.append("tnsPaiement", form.tnsPaiement);
    fd.append("tnsCaisseRetraite", form.tnsCaisseRetraite);
    fd.append("tnsCommentaire", form.tnsCommentaire.trim());
    fd.append("ppe", form.questionnaireAnswers[1] === "Oui");
    fd.append(
      "doutesIntegriteReputation",
      form.questionnaireAnswers[2] === "Oui"
    );
    fd.append("gelDesAvoirs", form.questionnaireAnswers[3] === "Oui");
    fd.append("clientIds", JSON.stringify(selectedClients.map((c) => c.id)));
    fd.append("accesLink", accesLink);
    if (accesLink) fd.append("mdp", linkMdp.trim());

    // Upload direct navigateur → Blob (contourne la limite 4,5 Mo), avec
    // une progression affichée pendant l'envoi.
    const uploadToAnnuaireBlob = (file, onProgress) =>
      uploadFileToBlob(file, "https://backend-myalfa.vercel.app/api/gi/annuaire/upload-token", onProgress);

    const filesToUpload = [
      ...(pieceIdentiteFile ? [{ kind: "pieceIdentite", file: pieceIdentiteFile }] : []),
      ...(extraitRBEFile ? [{ kind: "extraitRBE", file: extraitRBEFile }] : []),
      ...autresJustificatifs.map((file) => ({ kind: "autre", file })),
    ];

    const autresJustificatifsUrls = [];
    if (filesToUpload.length > 0) {
      for (let i = 0; i < filesToUpload.length; i++) {
        const { kind, file } = filesToUpload[i];
        const url = await uploadToAnnuaireBlob(file, ({ percentage }) =>
          setUploadProgress(Math.round((i / filesToUpload.length) * 100 + percentage / filesToUpload.length))
        );
        if (kind === "pieceIdentite") fd.append("pieceIdentiteUrl", url);
        else if (kind === "extraitRBE") fd.append("extraitRBEUrl", url);
        else autresJustificatifsUrls.push(url);
      }
      setUploadProgress(null);
    }
    if (autresJustificatifsUrls.length > 0) {
      fd.append("autresJustificatifsUrls", JSON.stringify(autresJustificatifsUrls));
    }

    QUESTIONS.forEach((_, i) => {
      fd.append(`question_${i + 1}_reponse`, form.questionnaireAnswers[i]);
      fd.append(`question_${i + 1}_commentaire`, form.questionnaireComments[i]);
    });
    fd.append("commentaireGlobal", form.commentaireGlobal);

    try {
      if (isEditMode) {
        const { data: saved } = await httpClient.put(`${ANNUAIRE_API}/${contactId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const updatedContact = {
          ...contact,
          nom: form.nom,
          prenoms: form.prenoms,
          dateNaissance: form.dateNaissance,
          villeNaissance: form.villeNaissance,
          paysNaissance: form.paysNaissance,
          rue: form.rue,
          codePostal: form.codePostal,
          villeAdresse: form.villeAdresse,
          paysAdresse: form.paysAdresse,
          email: form.email,
          telephone: form.telephone,
          dateEmission: form.dateEmission,
          dateExpiration: form.dateExpiration,
          autresMissions: form.autresMissions,
          nationalite: form.nationalite,
          autresAdresses: form.autresAdresses,
          identiteVerifiee: form.identiteVerifiee,
          autreActivite: form.autreActivite,
          provenance: form.provenance,
          estTNS: form.estTNS,
          tnsPeriodicite: form.tnsPeriodicite,
          tnsRegime: form.tnsRegime,
          tnsOrganisme: form.tnsOrganisme,
          tnsPaiement: form.tnsPaiement,
          tnsCaisseRetraite: form.tnsCaisseRetraite,
          tnsCommentaire: form.tnsCommentaire.trim(),
          // Historique complété côté serveur lors d'un changement de estTNS.
          tnsHistorique: saved?.tnsHistorique ?? contact?.tnsHistorique,
          commentaireGlobal: form.commentaireGlobal,
          ppe: form.questionnaireAnswers[1] === "Oui",
          question1Reponse: form.questionnaireAnswers[0],
          question2Reponse: form.questionnaireAnswers[1],
          question3Reponse: form.questionnaireAnswers[2],
          question4Reponse: form.questionnaireAnswers[3],
          question1Commentaire: form.questionnaireComments[0],
          question2Commentaire: form.questionnaireComments[1],
          question3Commentaire: form.questionnaireComments[2],
          question4Commentaire: form.questionnaireComments[3],
          comptes: localComptes,
          clients: selectedClients.map((c) => ({ id: c.id, nom: c.nomClient })),
          mdp: saved?.mdp ?? contact?.mdp,
          accesLink: saved?.accesLink ?? accesLink,
          accesLinkDesactive: saved?.accesLinkDesactive ?? contact?.accesLinkDesactive,
          comptePortail: saved?.comptePortail ?? contact?.comptePortail,
        };
        if (saved?.avertissementLink) showStatusToast({ status: "error", titre: "MyALFA Link", message: saved.avertissementLink });
        if (onContactCreated) onContactCreated(updatedContact);
        else onBack();
      } else {
        const { data: created } = await httpClient.post(ANNUAIRE_API, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (created?.avertissementLink) showStatusToast({ status: "error", titre: "MyALFA Link", message: created.avertissementLink });
        if (localComptes.length > 0 && created?.id) {
          await Promise.allSettled(
            localComptes.map((c) =>
              httpClient.post(COMPTES_API, {
                type: c.type,
                diversLabel: c.diversLabel || null,
                identifiant: c.identifiant || null,
                motDePasse: c.motDePasse || null,
                annuaireId: created.id,
              })
            )
          );
        }
        if (onContactCreated) onContactCreated(created);
        else onBack();
      }
    } catch (err) {
      console.error("Annuaire POST:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setUploadProgress(null);
      setSaving(false);
    }
  };

  const handleAddCompte = async () => {
    if (!newCompteInForm.type) { setCompteFormErr("Sélectionnez un type."); return; }
    if (newCompteInForm.type === "DIVERS" && !newCompteInForm.diversLabel.trim()) {
      setCompteFormErr("Indiquez un nom pour ce compte Divers.");
      return;
    }
    setCompteFormErr("");
    const diversLabel = newCompteInForm.type === "DIVERS" ? newCompteInForm.diversLabel : null;
    if (isEditMode) {
      const tempId = `temp_${Date.now()}`;
      const optimistic = { _tempId: tempId, type: newCompteInForm.type, diversLabel, identifiant: newCompteInForm.identifiant || null, motDePasse: newCompteInForm.motDePasse || null };
      setLocalComptes((prev) => [...prev, optimistic]);
      setNewCompteInForm({ type: "", identifiant: "", motDePasse: "", diversLabel: "" });
      try {
        const { data } = await httpClient.post(COMPTES_API, {
          type: optimistic.type, diversLabel: optimistic.diversLabel, identifiant: optimistic.identifiant, motDePasse: optimistic.motDePasse, annuaireId: contactId,
        });
        setLocalComptes((prev) => prev.map((c) => c._tempId === tempId ? data : c));
      } catch {
        setLocalComptes((prev) => prev.filter((c) => c._tempId !== tempId));
        setCompteFormErr("Erreur lors de la création.");
      }
    } else {
      setLocalComptes((prev) => [...prev, { _localId: Date.now(), type: newCompteInForm.type, diversLabel, identifiant: newCompteInForm.identifiant || null, motDePasse: newCompteInForm.motDePasse || null }]);
      setNewCompteInForm({ type: "", identifiant: "", motDePasse: "", diversLabel: "" });
    }
  };

  const handleUpdateCompteInForm = async (compte) => {
    const key = compte.id ?? compte._localId;
    const updated = {
      ...compte,
      identifiant: editingCompteDataInForm.identifiant,
      motDePasse: editingCompteDataInForm.motDePasse,
      ...(compte.type === "DIVERS" && { diversLabel: editingCompteDataInForm.diversLabel }),
    };
    setLocalComptes((prev) => prev.map((c) => (c.id ?? c._localId) === key ? updated : c));
    setEditingCompteKeyInForm(null);
    if (isEditMode && compte.id) {
      try {
        const { data } = await httpClient.put(`${COMPTES_API}/${compte.id}`, {
          identifiant: editingCompteDataInForm.identifiant,
          motDePasse: editingCompteDataInForm.motDePasse,
          ...(compte.type === "DIVERS" && { diversLabel: editingCompteDataInForm.diversLabel }),
        });
        setLocalComptes((prev) => prev.map((c) => c.id === compte.id ? data : c));
      } catch {
        setLocalComptes((prev) => prev.map((c) => c.id === compte.id ? compte : c));
      }
    }
  };

  const handleDeleteCompteInForm = async (compte) => {
    const label = compte ? getCompteTypeLabel(compte) : "ce compte";
    const confirmed = await dialog.confirm(
      `Supprimer "${label}" ?`,
      { variant: "danger", confirmLabel: "Supprimer" }
    );
    if (!confirmed) return;
    if (isEditMode && compte.id) {
      setLocalComptes((prev) => prev.filter((c) => c.id !== compte.id));
      try {
        await httpClient.delete(`${COMPTES_API}/${compte.id}`);
      } catch {
        setLocalComptes((prev) => [...prev, compte]);
      }
    } else {
      setLocalComptes((prev) => prev.filter((c) => (c._localId ?? c._tempId) !== (compte._localId ?? compte._tempId)));
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3 shrink-0 border-b border-[#f0e8dc]"
        style={{ background: "#fef6ee" }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 transition-colors"
          >
            <ChevronRight className="w-3 h-3" />
            Retour
          </button>
          <h3 className="text-sm font-bold text-slate-900 m-0">
            {isEditMode
              ? `Modifier — ${contact?.prenoms || ""} ${
                  contact?.nom || ""
                }`.trim()
              : "Nouvel associé / bénéficiaire effectif"}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            disabled={saving}
            className="text-[11px] text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-40"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 text-[11px] text-white px-3 py-1.5 rounded-lg bg-[#7A1533] hover:bg-[#5f1028] transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <svg
                className="w-3 h-3 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            ) : (
              <Check className="w-3 h-3" />
            )}
            {saving
              ? (uploadProgress !== null ? `Envoi du document... ${uploadProgress}%` : "Enregistrement…")
              : "Enregistrer"}
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div
        className="flex-1 overflow-y-auto px-6 py-4 space-y-3"
        style={{ background: "#f9f7f5" }}
      >
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs text-rose-700 flex items-center gap-2">
            <span className="shrink-0">⚠</span>
            {error}
          </div>
        )}
        {/* IDENTITÉ */}
        <SectionCard icon={User} title="Identité">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field
              label="Nom"
              required
              half
              value={form.nom}
              onChange={setField("nom")}
              hasError={!!fieldErrors.nom}
            />
            <Field
              label="Prénom(s)"
              required
              half
              value={form.prenoms}
              onChange={setField("prenoms")}
              hasError={!!fieldErrors.prenoms}
            />
            <Field
              label="Date de naissance"
              placeholder="JJ/MM/AAAA"
              required
              half
              value={form.dateNaissance}
              onChange={setField("dateNaissance")}
              hasError={!!fieldErrors.dateNaissance}
            />
            <Field
              label="Ville de naissance"
              half
              value={form.villeNaissance}
              onChange={setField("villeNaissance")}
            />
            <Field
              label="Pays de naissance"
              half
              value={form.paysNaissance}
              onChange={setField("paysNaissance")}
            />
            <Field
              label="Nationalité"
              half
              value={form.nationalite}
              onChange={setField("nationalite")}
            />
            <div className="col-span-2">
              <label className="block text-[11px] text-slate-500 mb-1.5">
                Provenance
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {["Site", "Recommandation", "Achalandage", "Autre"].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, provenance: opt }))}
                    className={`text-[11px] px-3 py-1 rounded-lg border transition-colors ${
                      form.provenance === opt
                        ? "border-[#7A1533] bg-[#7A1533] text-white font-medium"
                        : "border-slate-200 text-slate-500 hover:border-[#7A1533]/40"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ADRESSE DE RÉSIDENCE */}
        <SectionCard icon={FileText} title="Adresse de résidence">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div className="col-span-2">
              <Field
                label="Rue / numéro"
                value={form.rue}
                onChange={setField("rue")}
              />
            </div>
            <div className="col-span-2">
              <Field
                label="Autres adresses"
                placeholder="Adresse secondaire, boîte postale…"
                value={form.autresAdresses}
                onChange={setField("autresAdresses")}
              />
            </div>
            <Field
              label="Code postal"
              half
              value={form.codePostal}
              onChange={setField("codePostal")}
            />
            <Field
              label="Ville"
              half
              value={form.villeAdresse}
              onChange={setField("villeAdresse")}
            />
            <div>
              <Field
                label="Pays"
                value={form.paysAdresse}
                onChange={setField("paysAdresse")}
              />
            </div>
          </div>
        </SectionCard>

        {/* TNS */}
        <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
                TNS
              </span>
            </div>
            <ToggleSwitch
              checked={form.estTNS}
              onChange={async (v) => {
                // Confirmation seulement pour une fiche déjà suivie en TNS.
                if (!v && contact?.estTNS) {
                  const ok = await dialog.confirm(
                    "Voulez-vous vraiment désactiver le suivi TNS ? Les informations seront conservées et consultables en réactivant le suivi ou dans l'historique.",
                    { title: "Désactiver le suivi TNS", confirmLabel: "Désactiver", variant: "warning" }
                  );
                  if (!ok) return;
                }
                setForm((f) => ({ ...f, estTNS: v }));
              }}
            />
          </div>
          {form.estTNS && (
            <div className="space-y-2.5 mt-4 pt-4 border-t border-slate-100">
              <div>
                <span className="block text-[11px] text-slate-500 mb-1.5">
                  Périodicité
                </span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {TNS_PERIODICITES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, tnsPeriodicite: p }))}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border transition-colors ${
                        form.tnsPeriodicite === p
                          ? "border-[#7A1533] bg-[#7A1533] text-white font-medium"
                          : "border-slate-200 text-slate-500 hover:border-[#7A1533]/40"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <label className="col-span-2">
                  <span className="block text-[11px] text-slate-500 mb-1">Régime</span>
                  <input
                    value={form.tnsRegime}
                    onChange={(e) => setForm((f) => ({ ...f, tnsRegime: e.target.value }))}
                    placeholder="Ex. : praticien et auxiliaire médical, artisan..."
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
                  />
                </label>
                <label>
                  <span className="block text-[11px] text-slate-500 mb-1">Caisse de cotisation</span>
                  <select
                    value={form.tnsOrganisme}
                    onChange={(e) => setForm((f) => ({ ...f, tnsOrganisme: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
                  >
                    <option value="">—</option>
                    {withCurrent(TNS_ORGANISMES, form.tnsOrganisme).map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="block text-[11px] text-slate-500 mb-1">Caisse ou régime de retraite</span>
                  <select
                    value={form.tnsCaisseRetraite}
                    onChange={(e) => setForm((f) => ({ ...f, tnsCaisseRetraite: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
                  >
                    <option value="">—</option>
                    {withCurrent(TNS_CAISSES_RETRAITE, form.tnsCaisseRetraite).map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="block text-[11px] text-slate-500 mb-1">Mode de paiement</span>
                  <select
                    value={form.tnsPaiement}
                    onChange={(e) => setForm((f) => ({ ...f, tnsPaiement: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
                  >
                    <option value="">—</option>
                    {withCurrent(TNS_PAIEMENTS, form.tnsPaiement).map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <label className="col-span-2">
                  <span className="block text-[11px] text-slate-500 mb-1">Commentaire TNS</span>
                  <textarea
                    rows={2}
                    value={form.tnsCommentaire}
                    onChange={(e) => setForm((f) => ({ ...f, tnsCommentaire: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 resize-y"
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* IR */}
        <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
                IR
              </span>
            </div>
            <ToggleSwitch
              checked={form.autresMissions}
              onChange={(v) => setForm((f) => ({ ...f, autresMissions: v }))}
            />
          </div>
        </div>

        {/* CONTACT */}
        <SectionCard icon={Bell} title="Contact">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field
              label="Email"
              type="email"
              half
              value={form.email}
              onChange={setField("email")}
              note="(à renseigner pour activer le compte MyAlfa Link)"
            />
            <Field
              label="Téléphone"
              type="tel"
              half
              value={form.telephone}
              onChange={setField("telephone")}
            />
          </div>
        </SectionCard>

        {/* PIÈCE D'IDENTITÉ */}
        <SectionCard icon={CreditCard} title="Pièce d'identité">
          <div className="space-y-3">
            <UploadZone
              label="Charger la pièce d'identité"
              subtitle="CNI, passeport ou titre de séjour — recto/verso"
              required
              file={pieceIdentiteFile}
              onChange={setPieceIdentiteFile}
              existingUrl={contact?.pieceIdentite}
            />
            <div className="grid grid-cols-2 gap-x-4">
              <Field
                label="Date d'émission"
                placeholder="JJ/MM/AAAA"
                half
                value={form.dateEmission}
                onChange={setField("dateEmission")}
              />
              <Field
                label="Date d'expiration"
                placeholder="JJ/MM/AAAA"
                half
                value={form.dateExpiration}
                onChange={setField("dateExpiration")}
              />
            </div>
            <label className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-[#7A1533]/25 bg-[#fff6ee] cursor-pointer hover:bg-[#7A1533]/5 transition-colors mt-1">
              <input
                type="checkbox"
                checked={form.identiteVerifiee}
                onChange={(e) =>
                  setForm((f) => ({ ...f, identiteVerifiee: e.target.checked }))
                }
                className="w-4 h-4 rounded border-slate-300 accent-[#7A1533]"
              />
              <div className="flex-1">
                <p className="text-xs font-bold text-[#7A1533]">
                  Identité vérifiée
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  L'identité de cette personne a été contrôlée et validée
                </p>
              </div>
              {form.identiteVerifiee && (
                <Check className="w-4 h-4 text-emerald-600 shrink-0" />
              )}
            </label>
          </div>
        </SectionCard>

        {/* EXTRAIT RBE */}
        <SectionCard icon={Shield} title="Extrait RBE">
          <UploadZone
            label="Charger l'extrait RBE"
            subtitle="Registre des bénéficiaires effectifs — extrait à jour"
            file={extraitRBEFile}
            onChange={setExtraitRBEFile}
            existingUrl={contact?.extraitRBE}
          />
        </SectionCard>

        {/* AUTRES JUSTIFICATIFS */}
        <SectionCard
          icon={FileText}
          title="Autres justificatifs"
          suffix="(facultatif)"
        >
          <div className="space-y-2">
            {autresJustificatifs.map((file, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-[11px] text-slate-600 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50"
              >
                <FileText className="w-3 h-3 text-slate-400 shrink-0" />
                <span className="truncate">{file.name}</span>
              </div>
            ))}
            <button
              onClick={() => justificatifInputRef.current.click()}
              className="flex items-center gap-1.5 text-[11px] text-slate-600 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <Plus className="w-3 h-3" />
              Ajouter un justificatif
            </button>
            <input
              ref={justificatifInputRef}
              type="file"
              className="hidden"
              onChange={handleAddJustificatif}
            />
          </div>
        </SectionCard>

        {/* DOSSIERS LIÉS */}
        <SectionCard icon={FileText} title="Dossiers liés du portefeuille">
          <div className="space-y-3">
            <div className="space-y-2">
              {/* Chips des clients sélectionnés */}
              {selectedClients.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedClients.map((c) => (
                    <span
                      key={c.id}
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-[#7A1533]/30 bg-[#fff6ee] text-[#7A1533] font-medium"
                    >
                      #{c.nomClient?.toUpperCase()}
                      <button
                        onClick={() => removeClient(c.id)}
                        className="hover:text-[#5f1028] transition-colors"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Champ de recherche */}
              <div className="relative">
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] bg-white transition-colors ${
                    clientError ? "border-rose-300" : "border-slate-200"
                  }`}
                >
                  <Plus className="w-3 h-3 text-slate-400 shrink-0" />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={handleClientSearch}
                    placeholder="Ajouter un dossier — rechercher un client…"
                    className="flex-1 bg-transparent focus:outline-none text-slate-600 placeholder:text-slate-300"
                  />
                </div>
                {clientSuggestions.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-40 overflow-y-auto">
                    {clientSuggestions.map((c) => (
                      <li
                        key={c.id}
                        onClick={() => selectClient(c)}
                        className="px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors"
                      >
                        {c.nom}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {clientError && (
                <p className="text-[10px] text-rose-500">
                  Associez au moins un dossier client
                </p>
              )}
            </div>
          </div>
        </SectionCard>

        {/* QUESTIONNAIRE ASSOCIÉ / BE */}
        <SectionCard icon={Shield} title="Questionnaire associé / BE">
          <div className="space-y-2">
            {QUESTIONS.map((q, i) => (
              <QuestionRow
                key={i}
                num={i + 1}
                question={q}
                answer={form.questionnaireAnswers[i]}
                onAnswerChange={(val) => setAnswer(i, val)}
                comment={form.questionnaireComments[i]}
                onCommentChange={(val) => setComment(i, val)}
              />
            ))}

            <div className="mt-3">
              <label className="block text-[11px] text-slate-400 mb-1">
                Autre activité / informations complémentaires
              </label>
              <input
                type="text"
                value={form.autreActivite}
                onChange={setField("autreActivite")}
                placeholder="Ex. gérant d'une autre société, activité libérale…"
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 bg-white"
              />
            </div>

            <div className="mt-3">
              <label className="block text-[11px] text-slate-400 mb-1">
                Commentaire global
              </label>
              <textarea
                rows={3}
                value={form.commentaireGlobal}
                onChange={setField("commentaireGlobal")}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-700 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 bg-white"
              />
            </div>
          </div>
        </SectionCard>

        {/* COMPTES */}
        <SectionCard icon={KeyRound} title="Comptes" suffix="(optionnel)">
          <div className="space-y-3">
            {/* Formulaire ajout */}
            <div className="space-y-2">
              <div>
                <label className="block text-[10px] text-slate-400 mb-1">Type de compte</label>
                <select
                  value={newCompteInForm.type}
                  onChange={(e) => setNewCompteInForm((p) => ({ ...p, type: e.target.value }))}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 bg-white"
                >
                  <option value="">— Sélectionner un type —</option>
                  {Object.entries(COMPTE_TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              {newCompteInForm.type === "DIVERS" && (
                <div>
                  <label className="block text-[10px] text-slate-400 mb-1">Nom du compte</label>
                  <input
                    type="text"
                    value={newCompteInForm.diversLabel}
                    onChange={(e) => setNewCompteInForm((p) => ({ ...p, diversLabel: e.target.value }))}
                    placeholder="ex : Logiciel de facturation"
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 bg-white"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-slate-400 mb-1">Identifiant</label>
                  <input
                    type="text"
                    value={newCompteInForm.identifiant}
                    onChange={(e) => setNewCompteInForm((p) => ({ ...p, identifiant: e.target.value }))}
                    placeholder="Optionnel"
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-400 mb-1">Mot de passe</label>
                  <input
                    type="text"
                    value={newCompteInForm.motDePasse}
                    onChange={(e) => setNewCompteInForm((p) => ({ ...p, motDePasse: e.target.value }))}
                    placeholder="Optionnel"
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 bg-white"
                  />
                </div>
              </div>
              {compteFormErr && (
                <p className="text-[11px] text-rose-600">{compteFormErr}</p>
              )}
              <button
                type="button"
                onClick={handleAddCompte}
                className="flex items-center gap-1.5 text-[11px] text-[#7A1533] px-3 py-1.5 rounded-lg border border-[#7A1533]/40 hover:bg-[#fff6ee] transition-colors font-medium"
              >
                <Plus className="w-3 h-3" />
                Ajouter à la liste
              </button>
            </div>

            {/* Liste des comptes */}
            {localComptes.length > 0 && (
              <div className="space-y-1.5 border-t border-slate-100 pt-3">
                {localComptes.map((compte) => {
                  const key = compte.id ?? compte._tempId ?? compte._localId;
                  const isTemp = !!compte._tempId;
                  const isEditing = editingCompteKeyInForm === key;
                  return isEditing ? (
                    <div key={key} className="rounded-lg border border-[#7A1533]/30 bg-white px-3 py-2.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-semibold text-[#7A1533] bg-[#fff6ee] border border-[#7A1533]/20 px-2 py-0.5 rounded-md">
                          {getCompteTypeLabel(compte)}
                        </span>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => handleUpdateCompteInForm(compte)}
                            className="flex items-center gap-1 text-[10px] text-white bg-[#7A1533] hover:bg-[#5f1028] px-2 py-1 rounded-md transition-colors"
                          >
                            <Check className="w-2.5 h-2.5" />
                            Enregistrer
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingCompteKeyInForm(null)}
                            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {compte.type === "DIVERS" && (
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-0.5">Nom du compte</label>
                          <input
                            type="text"
                            value={editingCompteDataInForm.diversLabel}
                            onChange={(e) => setEditingCompteDataInForm((p) => ({ ...p, diversLabel: e.target.value }))}
                            className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20"
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-0.5">Identifiant</label>
                          <input
                            type="text"
                            value={editingCompteDataInForm.identifiant}
                            onChange={(e) => setEditingCompteDataInForm((p) => ({ ...p, identifiant: e.target.value }))}
                            className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-400 mb-0.5">Mot de passe</label>
                          <input
                            type="text"
                            value={editingCompteDataInForm.motDePasse}
                            onChange={(e) => setEditingCompteDataInForm((p) => ({ ...p, motDePasse: e.target.value }))}
                            className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#7A1533]/20 font-mono"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={key}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-100 bg-slate-50 transition-opacity ${isTemp ? "opacity-50" : ""}`}
                    >
                      <span className="text-[10px] font-semibold text-[#7A1533] bg-[#fff6ee] border border-[#7A1533]/20 px-2 py-0.5 rounded-md flex-shrink-0 truncate max-w-[110px]">
                        {getCompteTypeLabel(compte)}
                      </span>
                      <span className="flex-1 text-[11px] text-slate-600 font-mono truncate">
                        {compte.identifiant || "—"}
                      </span>
                      {compte.motDePasse && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-[11px] text-slate-500 font-mono">
                            {showComptePassInForm[key] ? compte.motDePasse : "••••••"}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowComptePassInForm((p) => ({ ...p, [key]: !p[key] }))}
                            className="text-slate-300 hover:text-slate-500 transition-colors"
                          >
                            {showComptePassInForm[key]
                              ? <EyeOff className="w-3 h-3" />
                              : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                      )}
                      {!isTemp && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCompteKeyInForm(key);
                              setEditingCompteDataInForm({ identifiant: compte.identifiant || "", motDePasse: compte.motDePasse || "", diversLabel: compte.diversLabel || "" });
                            }}
                            className="flex-shrink-0 text-slate-300 hover:text-[#7A1533] transition-colors"
                            title="Modifier"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteCompteInForm(compte)}
                            className="flex-shrink-0 text-slate-300 hover:text-rose-500 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SectionCard>

        {/* Accès au portail client MyALFA Link (même login et mot de passe
            que l'onglet MyALFA Link de la fiche client) */}
        <div
          className={`rounded-2xl border-2 px-5 py-4 transition-colors ${
            accesLink ? "border-[#7A1533]/40 bg-[#7A1533]/[0.03]" : "border-dashed border-[#7A1533]/30 bg-white"
          } ${linkErr ? "ring-2 ring-rose-300" : ""}`}
        >
          <div className="flex items-center gap-3">
            <img src="/logos-pa/myalfa-link.jpg" alt="" className="h-9 w-9 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900">Accès à MyALFA Link</p>
              <p className="text-[11px] text-slate-500">Le contact se connecte au portail client avec son email et ce mot de passe.</p>
            </div>
            <ToggleSwitch
              checked={accesLink}
              onChange={(v) => {
                setAccesLink(v);
                setLinkErr(false);
              }}
            />
          </div>

          {accesLink ? (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field
                label="Email (identifiant de connexion)"
                type="email"
                placeholder="email@client.fr"
                value={form.email}
                onChange={setField("email")}
                required
                half
                hasError={linkErr && !form.email.trim()}
              />
              <div>
                <label className={`block text-[11px] mb-1 ${linkErr && !linkMdp.trim() && !mdpPortailConserve ? "text-rose-500 font-medium" : "text-slate-500"}`}>
                  Mot de passe{mdpPortailConserve ? "" : " *"}
                </label>
                <div className="relative">
                  <input
                    id="annuaire-link-mdp"
                    type={voirLinkMdp ? "text" : "password"}
                    value={linkMdp}
                    autoComplete="new-password"
                    onChange={(e) => setLinkMdp(e.target.value)}
                    placeholder={mdpPortailConserve ? "Inchangé (choisi par le contact)" : "Mot de passe du portail"}
                    className={`w-full border rounded-lg pl-3 pr-8 py-1.5 text-xs text-slate-700 placeholder:text-slate-300 focus:outline-none focus:ring-1 bg-white ${
                      linkErr && !linkMdp.trim() && !mdpPortailConserve ? "border-rose-300 focus:ring-rose-300/40" : "border-slate-200 focus:ring-[#7A1533]/20"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setVoirLinkMdp((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    title={voirLinkMdp ? "Masquer" : "Afficher"}
                  >
                    {voirLinkMdp ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {mdpPortailConserve && <p className="text-[10px] text-slate-400 mt-0.5">Le contact a choisi son mot de passe sur le portail : laissez vide pour le conserver.</p>}
              </div>
              <p className="col-span-2 flex items-start gap-1.5 text-[10px] text-slate-500">
                <Globe className="w-3 h-3 mt-px shrink-0 text-[#7A1533]" />
                Le mail d'invitation avec ces identifiants s'envoie depuis la fiche du contact.
              </p>
            </div>
          ) : (
            accesInitial && (
              <p className="mt-3 flex items-start gap-1.5 text-[11px] text-amber-700">
                <Info className="w-3.5 h-3.5 mt-px shrink-0" />
                L'accès du contact sera désactivé à l'enregistrement. Rien n'est supprimé : recochez la case pour le réactiver.
              </p>
            )
          )}
        </div>
      </div>
    </div>
  );
}
